import { addEntity, addComponent } from 'bitecs';
import { Position, Animal, AI, AI_STATE, ANIMAL_TYPES } from '../world.js';
import { Sprite as PixiSprite, Graphics, Container, SCALE_MODES } from 'pixi.js';
import { TILE_SIZE } from '../config.js';
import { Assets } from 'pixi.js';
import { getAIStateName } from '../systems/animals.js';

let app = null;

export function setApp(pixiApp) {
    console.log('[BEE] Setting PIXI app:', pixiApp ? 'valid object' : 'null/undefined');
    if (!pixiApp || !pixiApp.renderer) {
        console.error('[BEE] Invalid PIXI app provided');
        return;
    }
    app = pixiApp;
    console.log('[BEE] PIXI app set successfully with renderer:', app.renderer ? 'available' : 'missing');
    
    // Debug check for worldContainer
    if (app.worldContainer) {
        console.log('[BEE] worldContainer is available in app');
    } else {
        console.warn('[BEE] worldContainer is missing from app - bees may not render properly');
    }
}

export function createBee(world, x, y, hiveEntity = null, PositionComponent) {
    console.log('[BEE] Creating bee at position:', x, y);
    
    // Check if app is properly initialized
    if (!app) {
        console.error('[BEE] PIXI app not initialized! Call setApp() first before creating bees.');
        return null;
    }
    
    if (!app.renderer) {
        console.error('[BEE] PIXI app renderer not available');
        return null;
    }

    // Debug check for world container
    if (!app.worldContainer) {
        console.error('[BEE] worldContainer is missing from app - bee will not be visible!');
        // Try to find it from the app.stage instead
        const possibleWorldContainer = app.stage.children.find(c => c.name === 'worldContainer');
        if (possibleWorldContainer) {
            console.log('[BEE] Found worldContainer in app.stage children');
            app.worldContainer = possibleWorldContainer;
        } else {
            console.error('[BEE] Could not find worldContainer - bee will not be visible!');
            return null;
        }
    }

    try {
        const bee = addEntity(world);
        console.log('[BEE] Created bee entity:', bee);

        // Add bee to world's entities array
        if (!world.entities) world.entities = [];
        world.entities.push(bee);

        // Add components
        addComponent(world, Position, bee);
        addComponent(world, Animal, bee);
        addComponent(world, AI, bee);

        // Set initial values
        Position.x[bee] = x;
        Position.y[bee] = y;
        Animal.type[bee] = ANIMAL_TYPES.BEE.id;
        Animal.energy[bee] = 100;
        Animal.weight[bee] = 1;
        Animal.age[bee] = 0;
        Animal.isFleeing[bee] = 0;
        Animal.isMoving[bee] = 1; // Start moving
        
        // Initialize with MOVING state and random target 
        AI.state[bee] = AI_STATE.MOVING;
        console.log('[BEE] Initial AI state set to:', getAIStateName(AI.state[bee]));
        
        // Set initial random movement target (slightly away from current position)
        const randomAngle = Math.random() * Math.PI * 2;
        const randomDistance = 50 + Math.random() * 100;
        AI.targetX[bee] = x + Math.cos(randomAngle) * randomDistance;
        AI.targetY[bee] = y + Math.sin(randomAngle) * randomDistance;
        
        AI.hiveEntity[bee] = hiveEntity; // Store reference to hive
        AI.hasNectar[bee] = 0; // Track if bee is carrying nectar
        AI.workTimer[bee] = 0; // Timer for work cycles
        AI.workDuration[bee] = 5000; // 5 seconds work cycle
        AI.timer[bee] = 0; // Initialize AI timer

        // Create a bee sprite with a container for better visuals
        const container = new Container();
        
        // Size for the bee (smaller than a tile)
        const beeSize = TILE_SIZE * 0.3;
        
        // Create sprite from bee image
        console.log('[BEE] Attempting to load bee texture...');
        const beeTexture = Assets.get('assets/images/animals/insects/bee.png');
        console.log('[BEE] Bee texture loaded:', beeTexture ? 'success' : 'failed', beeTexture);
        
        if (!beeTexture) {
            console.error('[BEE] Failed to load bee texture! Available assets:', Assets.cache.get());
            return null;
        }
        
        const sprite = new PixiSprite(beeTexture);
        console.log('[BEE] Created sprite with dimensions:', sprite.width, 'x', sprite.height);
        sprite.anchor.set(0.5);
        // Scale the sprite while maintaining aspect ratio
        const scale = beeSize / Math.max(sprite.width, sprite.height);
        sprite.scale.set(scale);
        sprite.position.set(x, y);
        
        // Add sprite to world container
        if (app.worldContainer) {
            app.worldContainer.addChild(sprite);
            console.log('[BEE] Added bee sprite to worldContainer');
        } else {
            console.error('[BEE] No worldContainer available for bee sprite');
            return null;
        }
        
        // Store sprite in world object
        if (!world.sprites) world.sprites = {};
        world.sprites[bee] = sprite;

        // Add update function to handle sprite flipping
        if (!world.spriteUpdates) world.spriteUpdates = {};
        world.spriteUpdates[bee] = () => {
            const dx = AI.targetX[bee] - Position.x[bee];
            if (dx !== 0) {
                sprite.scale.x = Math.sign(dx) * scale;
            }
        };

        console.log('[BEE] Bee entity created successfully:', {
            id: bee,
            position: { x, y },
            sprite: 'sprite created',
            hive: hiveEntity
        });

        return bee;
    } catch (error) {
        console.error('[BEE] Error creating bee entity:', error);
        return null;
    }
} 